import { Directive, ElementRef, Input, NgZone, OnDestroy, ViewChild, inject } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Klarna } from '@df/module-klarna/klarna.namespace';
import { KlarnaService } from '@df/module-klarna/klarna.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription } from 'rxjs';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class KlarnaPaymentComponentDef extends AbstractCheckoutPayment implements OnDestroy {
    @Input({
        required: true
    })
    methodCode!: string;

    protected klarnaService = inject(KlarnaService);

    protected initiated = false;

    protected subscription = new Subscription();

    protected lastLoadData = '';

    /**
     * We need element with #klarnacontainer in template
     */
    @ViewChild('klarnacontainer')
    protected klarnaContainer?: ElementRef<any>;

    protected ngZone = inject(NgZone);

    protected timeoutService = inject(TimeoutService);

    protected update?: () => void;

    override ngOnInit() {
        this.update = this.timeoutService.debounce(() => this._update(), 1000);
        super.ngOnInit();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.subscription.unsubscribe();
    }

    protected getJsAssets(): string[] {
        return [Klarna.Scripts.JS_SDK];
    }

    /**
     * Sets subscriptions, we has to update widget on totals and addresses changes
     */
    protected setSubscriptions(): void {
        this.subscription.add(this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => this.loadWidget));
        this.subscription.add(
            this.app.events.on(Checkout.EVENTS.StepChange).subscribe(() => {
                if (this.checkoutStepComponent?.isCurrent) {
                    this.update?.();
                }
            })
        );
        this.checkoutStepComponent?.forms.forEach(form => this.subscription.add(form.valueChanges!.subscribe(() => this.update?.())));
    }

    /**
     * Returns html element for klarna container
     * @returns {HTMLElement | undefined}
     */
    protected get klarnaContainerElement(): HTMLElement | undefined {
        return this.klarnaContainer && this.klarnaContainer.nativeElement;
    }

    /**
     * On script load success callback
     */
    protected override onScriptLoadSuccess(): void {
        this.klarnaService
            .loadConfig(this.methodCode as Klarna.Methods)
            .then(response => {
                if (response.klarna) {
                    this.initWidget(response.klarna);
                } else {
                    this.scriptStatus.error = Klarna.KLARNA_NOT_AVAILABLE_ERROR;
                }
            })
            .catch(() => {
                this.scriptStatus.error = Klarna.KLARNA_NOT_AVAILABLE_ERROR;
            });
    }

    /**
     * Initializes klarna widget
     * @param {Klarna.IConfig} config
     */
    protected initWidget(config: Klarna.IConfig): void {
        const payments = this.klarnaService.getPayments();
        const { enabled, client_token } = config;
        const container = this.klarnaContainerElement;
        if (enabled && client_token && payments && container) {
            payments.init({ client_token });
            super.onScriptLoadSuccess();
            this.initiated = true;
            this.loadWidget();
            this.setSubscriptions();
        } else {
            this.scriptStatus.error = Klarna.KLARNA_NOT_AVAILABLE_ERROR;
        }
        this.detectChanges();
    }

    /**
     * Loads klarna widget
     */
    protected loadWidget(): void {
        if (this.initiated) {
            const payments = this.klarnaService.getPayments();
            const container = this.klarnaContainerElement;
            const data = this.getLoadData();
            this.lastLoadData = JSON.stringify(data);
            if (container) {
                payments?.load({ container, payment_method_category: this.klarnaService.getPaymentMethodCategory(this.methodCode as Klarna.Methods) }, data);
            }
        }
    }

    /**
     * Updates widget if required
     */
    protected _update(): void {
        if (this.isUpdateRequired()) {
            this.loadWidget();
        }
    }

    /**
     * Checks if we have to update widget by sending new addresses
     * @returns {boolean}
     */
    protected isUpdateRequired(): boolean {
        return JSON.stringify(this.getLoadData()) !== this.lastLoadData;
    }

    /**
     * Returns widget load data
     * @returns {Klarna.IData}
     */
    protected getLoadData(): Klarna.IData {
        return {
            billing_address: this.klarnaService.getAddress(this.checkoutFormComponent.billingAddress, this.checkoutFormComponent.email),
            shipping_address: this.klarnaService.getAddress(this.checkoutFormComponent.shippingAddress, this.checkoutFormComponent.email)
        };
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns finalize request options
     * @returns {Klarna.IPaymentsFinalizeOptions}
     */
    protected getFinalizeOptions(): Klarna.IPaymentsFinalizeOptions {
        return { payment_method_category: this.klarnaService.getPaymentMethodCategory(this.methodCode as Klarna.Methods) };
    }

    /**
     * Returns autorize/finalize data
     * @param {Checkout.ISubmissionData} submissionData
     * @returns {Klarna.IPaymentsAuthorizeData}
     */
    protected getAuthorizeData(submissionData: Checkout.ISubmissionData): Klarna.IData {
        return {
            billing_address: this.klarnaService.getAddress(submissionData.billing_address, submissionData.email),
            shipping_address: this.klarnaService.getAddress(submissionData.shipping_address, submissionData.email)
        };
    }

    /**
     * Returns checkout submission payment data
     * @param {string} authorizationToken
     * @returns {Klarna.IPaymentsSubmissionPaymentData}
     */
    protected getSubmissionPaymentData(authorizationToken: string): Klarna.IPaymentsSubmissionPaymentData {
        return {
            method: this.methodCode as Klarna.Methods,
            additional_data: {
                authorization_token: authorizationToken
            }
        };
    }

    /**
     * Method letting you plug in your action before checkout submission
     * @returns {boolean | void}
     */
    protected override onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        checkoutSubmitBeforeEventData.continueSubmission = false;
        const status = this.checkoutFormComponent.status;
        status.busy = true;

        const finalizeOptions = this.getFinalizeOptions();
        const authorizeData = this.getAuthorizeData(checkoutSubmitBeforeEventData.data);

        this.klarnaService
            .authorize(finalizeOptions, authorizeData)
            .then(response => {
                if (response.authorization_token) {
                    checkoutSubmitBeforeEventData.data.paymentMethod = this.getSubmissionPaymentData(response.authorization_token);
                    status.data.busy = false;
                    checkoutSubmitBeforeEventData.next();
                } else {
                    status.error = this.klarnaService.getError();
                }
            })
            .catch(e => {
                if (typeof e === 'string') {
                    status.error = e;
                } else {
                    status.error = this.klarnaService.getError();
                }
            });
    }
}
